/**
 * MasterMeasureMultiSelect — Mehrfach-Zuordnung einer operativen Maßnahme zu
 * übergeordneten Maßnahmen (Vergaben). Optional — eine operative Maßnahme
 * kann auch ohne übergeordnete Maßnahme bestehen.
 */
import React, { useEffect, useState } from 'react';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { X, Layers } from 'lucide-react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../../components/ui/popover';
import { Checkbox } from '../../components/ui/checkbox';
import api from '../../lib/api';

export const MasterMeasureMultiSelect = ({ value = [], onChange }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get('/master-measures?is_active=true')
      .then((res) => setItems(res.data || []))
      .catch(() => setItems([]));
  }, []);

  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  };

  if (items.length === 0) {
    // Niemand hat bisher eine übergeordnete Maßnahme angelegt – Feld dezent ausblenden.
    return (
      <div className="text-xs text-muted-foreground">
        💡 Tipp: Übergeordnete Maßnahmen kannst du unter „Maßnahmen verwalten → Übergeordnete Maßnahmen&quot; anlegen und hier zuordnen.
      </div>
    );
  }

  const selected = items.filter((m) => value.includes(m.id));

  return (
    <div className="space-y-1.5" data-testid="master-measure-multiselect">
      <Label className="text-sm flex items-center gap-1.5">
        <Layers className="h-3.5 w-3.5" />
        Übergeordnete Maßnahme (optional)
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="w-full justify-start font-normal h-auto min-h-9 py-1.5"
          >
            {selected.length === 0 ? (
              <span className="text-muted-foreground">Keine Vergabe zugeordnet</span>
            ) : (
              <div className="flex flex-wrap gap-1">
                {selected.map((m) => (
                  <Badge key={m.id} variant="secondary" className="font-normal">
                    {m.name}
                    {m.vergabenummer && <span className="ml-1 text-muted-foreground text-[10px]">· {m.vergabenummer}</span>}
                  </Badge>
                ))}
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[420px] p-2 max-h-[320px] overflow-y-auto" align="start">
          <div className="space-y-1">
            {items.map((m) => {
              const checked = value.includes(m.id);
              return (
                <label
                  key={m.id}
                  className="flex items-start gap-2 p-2 rounded-md cursor-pointer hover:bg-secondary/40"
                >
                  <Checkbox checked={checked} onCheckedChange={() => toggle(m.id)} className="mt-0.5" />
                  <div className="flex-1 min-w-0 text-sm">
                    <div className="font-medium truncate">{m.name}</div>
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-2">
                      {m.vergabenummer && <span>Vergabe-Nr. {m.vergabenummer}</span>}
                      {m.los_nr && <span>· Los {m.los_nr}</span>}
                      {m.lfd_nr && <span>· Lfd. {m.lfd_nr}</span>}
                      {m.massnahmenummer && <span>Maßnahmen-Nr. {m.massnahmenummer}</span>}
                      {m.auftraggeber_name && <span>· {m.auftraggeber_name}</span>}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
          {value.length > 0 && (
            <div className="pt-2 border-t mt-2 flex justify-end">
              <Button variant="ghost" size="sm" onClick={() => onChange([])} type="button">
                <X className="h-3.5 w-3.5 mr-1" />
                Auswahl leeren
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};
